/** @format */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reducer/store";
import { Header } from "semantic-ui-react";
import { api, ui, utils } from "services";
import { bulkSetUserInfo } from "reducer/user.slice";
import {
  removeAuthInfo,
  setAuthInfos,
  clearAuthInfos,
} from "reducer/wallet.slice";
import {
  AuthEntity,
  AuthInterface,
  CustomerInterface,
  LoginType,
  Size,
} from "interfaces";
import CustomButton from "components/button";
import ChangePasswordPopup from "./change-password";
import TwoFactorAuthenticator from "./two-factor";
import CustomTable from "components/table";
import AddFarmer from "./add-farmer";
import EditFarmerPopup from "./edit-farmer";
import { useNavigate } from "react-router-dom";
import { ExtensionLinkComponent } from "components/extension";
import Config from "config";

export default function AccountPage() {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);
  const wallets = useSelector((state: RootState) => state.wallets);
  const loginType = useSelector((state: RootState) => state.logintype);

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [enabled2FA, setEnable2FA] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [tfaLoading, set2FALoading] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [addFarmer, setAddFarmer] = useState(false);
  const [walletComponents, setWalletComponents] = useState(<></>);
  const [qrCodeData, setQrCode] = useState(null);
  const [refetch, setRefetch] = useState(false);
  const [lastUpdatePw, setLastUpdatePw] = useState("mm/dd/yy");
  const [editIdentity, setEditIdentity] = useState(null);
  const dispatch = useDispatch();

  const dataLayout = (children: {
    mag?: string;
    mstr?: string;
    evol?: string;
  }) => (
    <div className="grid grid-rows-2 min-w-[100px]">
      <div>
        <img
          src="/assets/images/items/mag-icon.webp"
          alt="mag"
          className="inline-block mr-2 hidden md:inline-block h-6 w-6"
        />
        <span className="text-sm">{children?.mag ?? "0"} MAG</span>
      </div>
      <div className="mt-1">
        <img
          src="/assets/images/items/mstr-icon.svg"
          alt="mstr"
          className="inline-block mr-2 hidden md:inline-block h-6 w-6"
        />
        <span className="text-sm">{children?.mstr ?? "0"} MSTR</span>
      </div>
      <div className="mt-1">
        <img
          src="/assets/images/items/evo-item-icon.svg"
          alt="evo"
          className="inline-block mr-2 hidden md:inline-block h-6 w-6"
        />
        <span className="text-sm">{children?.evol ?? "0"} Evo pts</span>
      </div>
    </div>
  );

  const tableHeader = [
    {
      fieldName: "Name",
      className: "",
      fieldKey: AuthInterface.identity,
      render: (children: any, identity: string) => (
        <div
          onClick={() => {
            // nav(`/account/${identity}`)
          }}
        >
          {children}
        </div>
      ),
    },
    // {
    //   fieldName: "Today",
    //   className: "",
    //   fieldKey: "today",
    //   render: (children: any) => dataLayout(children)
    // },
    // {
    //   fieldName: "Yesterday",
    //   className: "",
    //   fieldKey: "yesterday",
    //   render: (children: any) => dataLayout(children)
    // },
    // {
    //   fieldName: "Average",
    //   className: "",
    //   fieldKey: "average",
    //   render: (children: any) => dataLayout(children)
    // },
    // {
    //   fieldName: "Total",
    //   className: "",
    //   fieldKey: "total",
    //   render: (children: any) => dataLayout(children)
    // },
    {
      fieldName: "",
      className: "",
    },
  ];

  const updateProfile = async (name: string) => {
    try {
      const data = await api.updateProfile(name);
      dispatch(bulkSetUserInfo(data));
      ui.alertSuccess("Change name", "Change name successful !");
    } catch (err: any) {
      ui.alertFailed("Change name", t(err.message));
    }
  };

  const requestChangeMail = async (mail: string) => {
    setRequestLoading(true);
    try {
      await api.requestChangeMail(mail);
      ui.alertSuccess("Change email", "Please check your email to confirm !");
      setRequestLoading(false);
    } catch (err: any) {
      ui.alertFailed("Change email", t(err.message));
      setRequestLoading(false);
    }
  };

  const request2FA = () => {
    ui.confirmAction(
      "Confirm action",
      <div className="text-left mb-1 text-lg">
        Please enter password to confirm you action.
      </div>,
      "password",
      confirmEnable2FA
    );
  };

  const disable2FA = () => {
    set2FALoading(true);
    api
      .disable2FA()
      .then((data: any) => {
        setEnable2FA(false);
        ui.alertSuccess(
          "Secondary authentication factor",
          "Disable second authentication factor successful !"
        );
        set2FALoading(false);
      })
      .catch((err: any) => {
        ui.alertFailed(
          "Secondary authentication factor failed !",
          t(err.message)
        );
        set2FALoading(false);
      });
  };

  const confirmEnable2FA = (code: string) => {
    api
      .checkPassword(code)
      .then((data: any) => {
        api
          .getQrCode2FA()
          .then((data) => setQrCode(data))
          .catch((err) => {
            ui.alertFailed("Get qr code failed !", t(err.message));
          });
      })
      .catch((err: any) => {
        ui.alertFailed(
          "Enable secondary authentication factor failed !",
          t(err.message)
        );
      });
  };

  const requestLinkEmail = async (email: string) => {
    try {
      await api.requestLinkEmail(email);
      ui.alertSuccess("Request link email", "Check your email to continue !");
    } catch (err: any) {
      ui.alertFailed("Request link email", t(err.message));
    }
  };

  const changeEmail = async (password: string) => {
    try {
      await api.checkPassword(password);
      ui.openTextForm("Change information", requestChangeMail, "Email");
    } catch (err: any) {
      ui.alertFailed("Confirm password", t(err.message));
    }
  };

  const unlinkEmail = async (password: string) => {
    try {
      await api.checkPassword(password);
      await api.removeEmail();
      dispatch(removeAuthInfo({ type: LoginType.Email }));
      ui.alertSuccess("Unlink email", "Remove email successful !");
      setEmail("");
    } catch (err: any) {
      ui.alertFailed("Unlink email failed !", t(err.message));
    }
  };
  useEffect(() => {
    if (user?.[CustomerInterface.id]) {
      api
        .getAuthInfos()
        .then((data) => {
          dispatch(clearAuthInfos());
          dispatch(setAuthInfos(data));
        })
        .catch((err) => {
          ui.alertFailed("Something went wrong !", "Please try again later !");
        });
    }
  }, [refetch]);

  useMemo(() => {
    setUserName(user?.[CustomerInterface.name] || "");
    const emailAuthInfo: AuthEntity = wallets[LoginType.Email];
    setEnable2FA(emailAuthInfo[AuthInterface.is_2fa_enabled]);
  }, [user, wallets]);

  useMemo(() => {
    const authInfo: AuthEntity = wallets[LoginType.Email];
    let email = authInfo[AuthInterface.identity];
    setEmail(email || "");
    let password_last_update = authInfo[AuthInterface.password_last_update];
    password_last_update && setLastUpdatePw(password_last_update.split("T")[0]);
  }, [wallets]);

  useEffect(() => {
    setWalletComponents(
      <ExtensionLinkComponent authInfos={wallets} size={Size.Big} />
    );
  }, [wallets, loginType]);

  return (
    <div className="w-full dao-container">
      <TwoFactorAuthenticator
        open={qrCodeData}
        onClose={() => {
          setQrCode(null);
        }}
        onSuccess={() => setEnable2FA(true)}
      />
      <ChangePasswordPopup
        open={changePassword}
        onClose={() => {
          setChangePassword(false);
        }}
      />
      <AddFarmer
        open={addFarmer}
        onClose={() => {
          setAddFarmer(false);
        }}
      />
      <EditFarmerPopup
        open={editIdentity !== null}
        onClose={() => {
          setEditIdentity(null);
        }}
        data={editIdentity}
        onSuccess={() => setRefetch(!refetch)}
      />
      <Header className="text-center">
        <span className="text-white text-3xl font-semibold">
          Account setting
        </span>
      </Header>
      <div className="grid grid-cols-12 mt-10">
        {false && (
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-black-secondary text-white-light py-6 lg:pb-10 px-6 rounded-lg h-full">
              <div className="card-header text-2xl mb-5 font-medium text-white ">
                General settings
              </div>
              <div className="flex justify-between py-3">
                <div>Display name</div>
                <div>{utils.formatName(userName)}</div>
              </div>
              <div className="br" />
              <div className="flex justify-between lg:grid lg:grid-rows-2 xl:flex xl:justify-between py-3 mb-3">
                <div>Email</div>
                <div className="flex">
                  {email}
                  {user?.[CustomerInterface.id] && (
                    <img
                      src={`/assets/images/icons/${
                        email ? "link-wallet" : "unlink-wallet"
                      }-icon.svg`}
                      alt="wallet-icon"
                      className="h-6 w-6 ml-2 cursor-pointer"
                      onClick={
                        email
                          ? () =>
                              ui.confirmAction(
                                "Confirm action",
                                "",
                                "password",
                                unlinkEmail
                              )
                          : () =>
                              ui.openTextForm(
                                "Connect to email",
                                requestLinkEmail,
                                "Email",
                                "email"
                              )
                      }
                    />
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <CustomButton
                  color="yellow"
                  disabled={!user?.[CustomerInterface.id]}
                  onClick={() => {
                    ui.openTextForm(
                      "Change information",
                      updateProfile,
                      "Name"
                    );
                  }}
                >
                  Change name
                </CustomButton>
                <CustomButton
                  color="yellow"
                  disabled={!user?.[CustomerInterface.id] || !email}
                  onClick={() => {
                    ui.confirmAction(
                      "Confirm action",
                      "",
                      "password",
                      changeEmail
                    );
                  }}
                >
                  Change email
                </CustomButton>
              </div>

              <div className="br-2 mt-9 mb-2"></div>
              <div className="sm:flex sm:justify-between lg:grid lg:grid-rows-2 xl:flex xl:justify-between py-3 mb-2">
                <div>Password</div>
                <div>Last modified: {lastUpdatePw}</div>
              </div>
              <CustomButton
                disabled={!user?.[CustomerInterface.id] || !email}
                color="yellow"
                onClick={() => {
                  setChangePassword(true);
                }}
              >
                Change password
              </CustomButton>

              <div className="br-2 mt-9 mb-4"></div>
              <div className="flex justify-between py-3 mb-2">
                <div>
                  The secondary <br />
                  authentication factor
                </div>
                <div className={enabled2FA ? "text-orange" : "text-red-500"}>
                  {enabled2FA ? "Enabled" : "Not enable"}
                </div>
              </div>
              <CustomButton
                loading={tfaLoading}
                color={enabled2FA ? "red" : "yellow"}
                disabled={!user?.[CustomerInterface.id] || !email}
                onClick={enabled2FA ? disable2FA : request2FA}
              >
                {enabled2FA ? "Disable" : "Enable"} authentication
              </CustomButton>
            </div>
          </div>
        )}

        <div className={`mt-10 col-span-12 lg:mt-0 lg:ml-6 h-30`}>
          <div className="bg-black-secondary text-white py-6 pb-10 px-8 rounded-lg h-full">
            <div className="card-header text-2xl mb-5 font-medium text-white ">
              Authentication methods
            </div>
            <div
              className={`gap-4 grid grid-cols-1 md:grid-cols-2 ${
                Config.MainnetUnsupportedChain.includes(LoginType.Email)
                  ? "xl:grid-cols-4 2xl:grid-cols-4"
                  : "xl:grid-cols-3 2xl:grid-cols-3"
              }`}
            >
              {user?.[CustomerInterface.id] && walletComponents}
            </div>
          </div>
        </div>
      </div>

      {!Config.MainnetUnsupportedChain.includes(LoginType.Farmer) && (
        <div className="text-center mx-auto w-full mt-10 text-white flex justify-between">
          <span className="text-2xl">Alternative login manager</span>
          <div className="text-center">
            <CustomButton
              className="px-8 inline-block"
              disabled={!user?.[CustomerInterface.id]}
              color="yellow"
              onClick={() => {
                setAddFarmer(true);
              }}
            >
              Add{" "}
              <span className="font-medium main-font-medium hidden lg:inline-block">
                new
              </span>
            </CustomButton>
          </div>
        </div>
      )}

      {!Config.MainnetUnsupportedChain.includes(LoginType.Farmer) && (
        <div className="mt-3 bg-black-secondary text-white-light rounded-lg pb-10">
          <div>
            <CustomTable
              className="min-h-[250px] max-h-[250px] overflow-y-scroll"
              tableHeader={tableHeader}
              tableData={wallets[LoginType.Farmer]}
              onDelete={api.deleteFarmerAccount}
              refetch={refetch}
              setRefetch={setRefetch}
              onEdit={(data: any) => setEditIdentity(data)}
              overrideHeader=""
            />
          </div>
        </div>
      )}
    </div>
  );
}
